import { api } from '../api/client';
import { API_EMAIL_DELIVERY_RESUME_URL, API_EMAIL_PREFERENCES_URL } from '../utils';

/**
 * keepr's side of email delivery that a PERSON touches (keepr-api
 * utils/emailEvents): turning mail back on after a spam report, signed in,
 * and the preferences link in the foot of every mail, signed out.
 *
 * An account's delivery state rides on `GET /api/user-info` as
 * `emailDelivery` — `{ state: 'bounced' | 'complained' | 'suppressed', reason,
 * address, at, until, suppressionId, resumeLockedUntil }`, absent while mail
 * flows — so it is read from the current user, not fetched here.
 */

/**
 * Turn email back on after a spam report (plan §3.5): releases the caller's
 * own complaint suppression and turns the Email master on in both stores.
 * Resolves `{ resumed, emailDelivery }`. Refused with 409 `not_resumable` (a
 * bounce, an admin's stop, another account's report) or `resume_locked` (a
 * second report — `unlocksAt` on the body).
 */
export async function resumeEmailDelivery() {
	const { data } = await api.post(API_EMAIL_DELIVERY_RESUME_URL);
	return data || { resumed: false, emailDelivery: null };
}

/**
 * What a preferences link shows: `{ address, notificationEmails,
 * emailDelivery, notification: { key, name, collection, muted, locked } | null,
 * export: null, manageUrl }`. `key` and `collectionId` are the notification
 * the mail was about (they ride on the link, and are not secrets). A dead link
 * of any kind is a flat 404.
 */
export async function getEmailPreferences(code, { key = null, collectionId = null } = {}) {
	const params = {};
	if (key) params.key = key;
	if (collectionId) params.c = collectionId;
	const { data } = await api.get(API_EMAIL_PREFERENCES_URL(code), { params });
	return data;
}

/** Turn notification emails off — the account's Email master. Idempotent. */
export async function turnOffEmailsFromLink(code) {
	await api.post(API_EMAIL_PREFERENCES_URL(code), { action: 'email-off' });
}

/**
 * Mute one notification for the link's owner — the account page's Mute, the
 * same row. A tier above that locks it answers 409.
 */
export async function muteFromLink(code, { key, collectionId = null }) {
	await api.post(API_EMAIL_PREFERENCES_URL(code), { action: 'mute', key, ...(collectionId ? { collection_id: collectionId } : {}) });
}
